import { useCallback, useMemo, useState } from "react";

export interface TableCheckedState {
  all: boolean;
  checkedIds: Set<string>;
  uncheckedIds: Set<string>;
}

const initialTableCheckedState: TableCheckedState = {
  all: false,
  checkedIds: new Set(),
  uncheckedIds: new Set(),
};

export function useTableCheckboxesState(disabledIds: Set<string> = new Set()) {
  const [tableCheckedState, setTableCheckedState] = useState<TableCheckedState>(
    initialTableCheckedState,
  );

  const isDisabled = useCallback(
    (id: string) => disabledIds.has(id),
    [disabledIds],
  );

  const isIndeterminate = useMemo(() => {
    const { all, checkedIds, uncheckedIds } = tableCheckedState;

    // Считаем только доступные (не disabled)
    if (all) {
      return uncheckedIds.size > 0;
    } else {
      const effectiveChecked = Array.from(checkedIds).filter(
        (id) => !isDisabled(id),
      );

      return effectiveChecked.length > 0;
    }
  }, [tableCheckedState, isDisabled]);

  const checkAll = useCallback(
    (checked: boolean) => {
      setTableCheckedState(() => {
        if (checked) {
          return {
            all: true,
            checkedIds: new Set(),
            uncheckedIds: new Set(disabledIds), // Не выделяем disabled
          };
        } else {
          return {
            all: false,
            checkedIds: new Set(),
            uncheckedIds: new Set(),
          };
        }
      });
    },
    [disabledIds],
  );

  const getChecked = useCallback(
    (id: string): boolean => {
      if (isDisabled(id)) return false;

      const { all, checkedIds, uncheckedIds } = tableCheckedState;

      return (all && !uncheckedIds.has(id)) || checkedIds.has(id);
    },
    [tableCheckedState, isDisabled],
  );

  const checkSingle = useCallback(
    (id: string, isChecked: boolean) => {
      if (isDisabled(id)) return;

      setTableCheckedState((prevState) => {
        const newUncheckedIds = new Set(prevState.uncheckedIds);
        const newCheckedIds = new Set(prevState.checkedIds);

        if (isChecked) {
          newUncheckedIds.delete(id);
          newCheckedIds.add(id);
        } else {
          newCheckedIds.delete(id);
          if (prevState.all) {
            newUncheckedIds.add(id);
          }
        }

        return {
          ...prevState,
          uncheckedIds: newUncheckedIds,
          checkedIds: newCheckedIds,
        };
      });
    },
    [isDisabled],
  );

  const resetTableCheckedState = useCallback(() => {
    setTableCheckedState(initialTableCheckedState);
  }, []);

  return {
    tableCheckedState,
    isIndeterminate,
    checkAll,
    getChecked,
    checkSingle,
    resetTableCheckedState,
  };
}
